import { useEffect, useRef, useState } from 'react';

export const TYPEWRITER_TICK_MS = 22;

export function useTypewriterContent({
  content,
  enabled,
  onComplete,
}: {
  content: string;
  enabled: boolean;
  onComplete?: () => void;
}) {
  const [visibleContent, setVisibleContent] = useState(() => (enabled ? '' : content));
  const onCompleteRef = useRef(onComplete);
  const hasCompletedRef = useRef(!enabled);
  const wasEnabledRef = useRef(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!enabled) {
      hasCompletedRef.current = true;
      wasEnabledRef.current = false;
      setVisibleContent(content);
      return undefined;
    }

    const justEnabled = !wasEnabledRef.current;
    wasEnabledRef.current = true;
    hasCompletedRef.current = false;
    setVisibleContent((currentContent) =>
      !justEnabled && content.startsWith(currentContent) ? currentContent : '',
    );

    const intervalId = window.setInterval(() => {
      setVisibleContent((currentContent) => {
        if (currentContent.length >= content.length) {
          window.clearInterval(intervalId);
          return content;
        }

        const remainingLength = content.length - currentContent.length;
        const nextStep =
          remainingLength > 600 ? 6 : remainingLength > 240 ? 4 : remainingLength > 80 ? 3 : 2;
        return content.slice(0, currentContent.length + nextStep);
      });
    }, TYPEWRITER_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [content, enabled]);

  useEffect(() => {
    if (!enabled || hasCompletedRef.current || visibleContent.length < content.length) {
      return;
    }

    hasCompletedRef.current = true;
    onCompleteRef.current?.();
  }, [content.length, enabled, visibleContent.length]);

  return enabled ? visibleContent : content;
}
